/**
 * Fixtures tab — list with filters, plus the preserved Reassign-fixture
 * edit (the only edit in the entire Content section).
 *
 * Reassign UX: each row has an "Edit round" button that opens a small
 * dialog with a round picker + optional reason field. Confirming hits
 * `PATCH /v1/admin/fixtures/:id/assigned-round` which the backend
 * audit-logs on success.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { isReadOnlyAdminMode } from '@/lib/admin-mode';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  type AdminFixtureRow,
  type LeagueName,
  useAdminFixtureRounds,
  useAdminFixtures,
  useCurrentFixtureRound,
  useGameDataOverview,
  useReassignFixtureRound,
  useSimulationRoundStatus,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  DateRange,
  FilterSelect,
  Pagination,
  formatTimestamp,
  toIsoEnd,
  toIsoStart,
} from './shared';
import { WeeklyFdrTable } from './WeeklyFdrTable';

const PAGE_SIZE = 100;

const STATUS_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'live', label: 'Live' },
  { value: 'completed', label: 'Completed' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'abandoned', label: 'Abandoned' },
];

// WC scope_code ordering — group MDs first, then knockout rounds. The
// backend lexsorts ('F' < 'MD1' < 'QF' < 'R16' < 'R32' < 'SF') which
// is meaningless to a user, so we re-order on the client.
const WC_SCOPE_ORDER = ['MD1', 'MD2', 'MD3', 'R32', 'R16', 'QF', 'SF', 'F'];
const WC_SCOPE_LABELS: Record<string, string> = {
  MD1: 'Group MD1',
  MD2: 'Group MD2',
  MD3: 'Group MD3',
  R32: 'Round of 32',
  R16: 'Round of 16',
  QF: 'Quarter-finals',
  SF: 'Semi-finals',
  F: 'Final & 3P',
  R08: 'Final & 3P',
};

function sortScopeCodes(codes: readonly string[]): string[] {
  const indexOf = (s: string) => {
    const i = WC_SCOPE_ORDER.indexOf(s);
    return i === -1 ? WC_SCOPE_ORDER.length : i;
  };
  return [...codes].sort((a, b) => indexOf(a) - indexOf(b) || a.localeCompare(b));
}

export function FixturesTab({ league }: { league: LeagueName }) {
  const readOnly = isReadOnlyAdminMode();
  // For EPL the filter is numeric `round` (R1–R38). For WC the dataset is
  // organised by `scope_code` (MD1..F), with knockout fixtures carrying
  // `round=0` because the SportsMonks round_id only resolves after the
  // FIFA bracket draw. Filtering by integer round is meaningless for WC
  // and produces "Stage 0" which the backend rejects (Zod min:1) — so we
  // switch the filter dimension on the league.
  //
  // Initial filter:
  //   EPL → 'current' (the existing useCurrentFixtureRound hook resolves
  //         this to the gameweek that's most recently in play)
  //   WC  → 'current' which we resolve client-side via the round-status
  //         query (smallest scope with at least one non-completed fixture)
  const [filter, setFilter] = useState<string>('current');
  const [status, setStatus] = useState<string>('any');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<AdminFixtureRow | null>(null);
  const [view, setView] = useState<'list' | 'weekly'>('list');
  const showWeekly = league === 'EPL' && view === 'weekly';

  const currentRoundQuery = useCurrentFixtureRound();
  const fixtureRounds = useAdminFixtureRounds(league);
  const wcRoundStatus = useSimulationRoundStatus(league === 'WC' ? 'WC' : 'EPL');
  const rawCurrentRound =
    currentRoundQuery.data?.round ?? currentRoundQuery.data?.current_round ?? null;
  // The backend returns `0` when no current round is known (e.g. empty DB or
  // off-season). Coerce to null so the filter is omitted instead of sending
  // `round=0`, which the fixtures Zod schema rejects as `< 1`.
  const currentRound =
    typeof rawCurrentRound === 'number' && rawCurrentRound > 0 ? rawCurrentRound : null;

  // For WC, derive the active scope from the simulation round-status
  // response (one row per WC scope, already sorted MD1..F). The active
  // scope is the first one where not every fixture has resolved; falls
  // back to the last scope with fixtures when the bracket is fully done.
  const wcActiveScope = useMemo(() => {
    if (league !== 'WC') return null;
    const rounds = wcRoundStatus.data?.data ?? [];
    const firstUnfinished = rounds.find(
      (r) => r.fixture_resolved < r.fixture_total,
    );
    if (firstUnfinished?.scope_code) return firstUnfinished.scope_code;
    const lastWithFixtures = [...rounds].reverse().find((r) => r.fixture_total > 0);
    return lastWithFixtures?.scope_code ?? null;
  }, [league, wcRoundStatus.data]);

  // EPL filter is numeric round; WC filter is scope_code string. The
  // backend `/v1/admin/fixtures` accepts both — we send whichever
  // dimension matches the active league. 'current' resolves per-league:
  // EPL → useCurrentFixtureRound's numeric value; WC → the active scope
  // computed from round-status (smallest scope with at least one
  // non-completed fixture).
  const selectedRound =
    league === 'WC'
      ? undefined
      : filter === 'current'
        ? (currentRound ?? undefined)
        : filter === 'any'
          ? undefined
          : Number(filter);
  const selectedScopeCode =
    league === 'WC'
      ? filter === 'any'
        ? undefined
        : filter === 'current'
          ? (wcActiveScope ?? undefined)
          : filter
      : undefined;

  const { data, isLoading, error } = useAdminFixtures({
    round: selectedRound,
    scope_code: selectedScopeCode,
    status: status === 'any' ? undefined : status,
    league,
    from: toIsoStart(from),
    to: toIsoEnd(to),
    limit: PAGE_SIZE,
    offset,
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  // EPL only — set of integer rounds for the filter dropdown. Drops 0
  // (sentinel for "no scoring round assigned"); for EPL there shouldn't
  // be any anyway.
  const knownRounds = useMemo(() => {
    if (league !== 'EPL') return [] as number[];
    const rounds = new Set<number>();
    for (const row of fixtureRounds.data?.data ?? []) {
      if (row.round > 0) rounds.add(row.round);
    }
    for (const row of rows) {
      if (row.round > 0) rounds.add(row.round);
      if (row.effective_round > 0) rounds.add(row.effective_round);
      if (row.assigned_round != null && row.assigned_round > 0) rounds.add(row.assigned_round);
    }
    return Array.from(rounds).sort((a, b) => a - b);
  }, [fixtureRounds.data?.data, rows, league]);

  // WC only — sorted list of distinct scope_codes (MD1..F).
  const knownScopeCodes = useMemo(() => {
    if (league !== 'WC') return [] as string[];
    const set = new Set<string>(fixtureRounds.data?.meta?.scope_codes ?? []);
    for (const row of rows) {
      if (row.scope_code) set.add(row.scope_code);
    }
    return sortScopeCodes(Array.from(set));
  }, [fixtureRounds.data?.meta?.scope_codes, rows, league]);

  const filterOptions = useMemo(() => {
    if (league === 'WC') {
      const currentLabel =
        wcActiveScope == null
          ? 'Current/upcoming'
          : `Current/upcoming (${wcActiveScope}${
              WC_SCOPE_LABELS[wcActiveScope] ? ' — ' + WC_SCOPE_LABELS[wcActiveScope] : ''
            })`;
      const opts: Array<{ value: string; label: string }> = [
        { value: 'current', label: currentLabel },
        { value: 'any', label: 'All scopes' },
      ];
      for (const code of knownScopeCodes) {
        opts.push({ value: code, label: `${code} — ${WC_SCOPE_LABELS[code] ?? code}` });
      }
      return opts;
    }
    const opts: Array<{ value: string; label: string }> = [
      {
        value: 'current',
        label: currentRound == null ? 'Current/upcoming' : `Current/upcoming (R${currentRound})`,
      },
      { value: 'any', label: 'All rounds' },
    ];
    for (const r of knownRounds) opts.push({ value: String(r), label: `R${r}` });
    return opts;
  }, [currentRound, knownRounds, knownScopeCodes, league, wcActiveScope]);

  return (
    <div className="space-y-4">
      {league === 'EPL' ? (
        <div className="flex items-center gap-2" role="group" aria-label="Fixture view">
          <Button type="button" size="sm" variant={showWeekly ? 'outline' : 'default'} aria-pressed={!showWeekly} onClick={() => setView('list')}>
            Fixture list
          </Button>
          <Button type="button" size="sm" variant={showWeekly ? 'default' : 'outline'} aria-pressed={showWeekly} onClick={() => setView('weekly')}>
            Weekly FDR
          </Button>
          {showWeekly ? <span className="text-xs text-muted-foreground">Full season · list filters preserved</span> : null}
        </div>
      ) : null}
      {showWeekly ? <WeeklyFixtureDifficulty /> : (
        <>
      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label={league === 'WC' ? 'Stage' : 'Round'}
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setOffset(0);
          }}
          options={filterOptions}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
          options={STATUS_OPTIONS}
        />
        <DateRange
          from={from}
          to={to}
          onChange={({ from: f, to: t }) => {
            setFrom(f);
            setTo(t);
            setOffset(0);
          }}
        />
      </div>
      {league === 'EPL' ? <FixtureDifficultyNote /> : null}

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminFixtureRow>
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            {
              key: 'round',
              label: 'Round',
              render: (r) => (
	                <span className="font-mono">
	                  {r.scope_code ?? `R${r.effective_round}`}
	                  {r.assigned_round != null && r.assigned_round !== r.round ? (
                    <span
                      className="ml-1 inline-block rounded border border-yellow-500/40 bg-yellow-500/10 px-1 text-[10px] font-semibold uppercase text-yellow-600"
                      title={`Originally R${r.round}, scoring in R${r.assigned_round}`}
                    >
                      R{r.round} → R{r.assigned_round}
                    </span>
                  ) : null}
                </span>
              ),
            },
            { key: 'home', label: 'Home', render: (r) => r.home_team },
            { key: 'away', label: 'Away', render: (r) => r.away_team },
            ...(league === 'WC'
              ? [
                  {
                    key: 'market',
                    label: 'Odds',
                    render: (r: AdminFixtureRow) => <MarketOddsCell fixture={r} />,
                  },
                ]
              : []),
            {
              key: 'fdr',
              label: 'FDR',
              render: (r) => <FixtureDifficultyCell fixture={r} />,
            },
            {
              key: 'kickoff',
              label: 'Kickoff',
              render: (r) => formatTimestamp(r.kickoff),
            },
            {
              key: 'status',
              label: 'Status',
              render: (r) => <StatusBadge status={r.status} />,
            },
            {
              key: 'score',
              label: 'Score',
              align: 'right',
              render: (r) => <FixtureScore fixture={r} />,
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (r) => (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={readOnly}
                  title={readOnly ? 'This preview cannot reassign fixtures' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (readOnly) return;
                    setEditing(r);
                  }}
                >
                  Reassign round
                </Button>
              ),
            },
          ]}
        />
        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </DataTableShell>

      <ReassignDialog
        fixture={editing}
        rounds={knownRounds}
        onClose={() => setEditing(null)}
      />
        </>
      )}
    </div>
  );
}

function WeeklyFixtureDifficulty() {
  const { data, isLoading, error } = useGameDataOverview('EPL');
  const dataset = data?.data;
  if (!dataset || isLoading || error) {
    return <DataTableShell loading={isLoading} error={error} empty={!dataset}><div /></DataTableShell>;
  }
  return <WeeklyFdrTable season={dataset.season} rounds={dataset.rounds} />;
}

function MarketOddsCell({ fixture }: { fixture: AdminFixtureRow }) {
  const oddsSources = fixture.odds_sources ?? [];
  const polymarket =
    oddsSources.find((source) => source.source === 'polymarket') ??
    (fixture.market_odds
      ? {
          source: fixture.market_odds.source ?? 'polymarket',
          bookmaker: null,
          home: fixture.market_odds.home,
          draw: fixture.market_odds.draw,
          away: fixture.market_odds.away,
          fetched_at: fixture.market_odds.fetched_at ?? '',
        }
      : null);
  const sportsmonks = oddsSources.filter((source) => source.source === 'sportsmonks');
  const sportsmonksAverage = averageOddsSources(sportsmonks);
  const visibleRows = [
    polymarket ? { label: 'Polymarket', odds: polymarket } : null,
    sportsmonksAverage
      ? {
          label: `SportsMonks avg (${sportsmonks.length})`,
          odds: sportsmonksAverage,
        }
      : null,
  ].filter(
    (row): row is { label: string; odds: { home: number; draw: number; away: number } } =>
      row !== null,
  );

  if (visibleRows.length === 0) {
    return <span className="font-mono text-muted-foreground">{DASH}</span>;
  }

  const bookmakerPreview = sportsmonks
    .map((source) => source.bookmaker)
    .filter((name): name is string => Boolean(name))
    .slice(0, 6)
    .join(', ');
  const title = [
    polymarket
      ? `Polymarket H=${formatPercent(polymarket.home)}, D=${formatPercent(
          polymarket.draw,
        )}, A=${formatPercent(polymarket.away)}`
      : null,
    sportsmonksAverage
      ? `SportsMonks ${sportsmonks.length} bookmaker rows${
          bookmakerPreview ? ` (${bookmakerPreview}${sportsmonks.length > 6 ? ', ...' : ''})` : ''
        }. Avg H=${formatPercent(sportsmonksAverage.home)}, D=${formatPercent(
          sportsmonksAverage.draw,
        )}, A=${formatPercent(sportsmonksAverage.away)}`
      : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <div
      className="flex min-w-[11rem] flex-col gap-1 font-mono text-xs"
      title={title}
    >
      {visibleRows.map((row) => {
        const outcomes = outcomeRows(fixture, row.odds);
        const leader = outcomes.reduce(
          (best, outcome) => (outcome.value > best.value ? outcome : best),
          outcomes[0],
        );
        return (
          <div key={row.label} className="space-y-0.5">
            <div className="flex items-center justify-between gap-3">
              <span className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                {row.label}
              </span>
              <span className="font-semibold tabular-nums">
                {leader.key} {formatPercent(leader.value)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-[10px] text-muted-foreground">
              {outcomes.map((outcome) => (
                <span
                  key={outcome.key}
                  className={cn(
                    'rounded border px-1 py-0.5 text-center tabular-nums',
                    outcome.key === leader.key
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                      : 'border-border',
                  )}
                  title={`${outcome.label}: ${formatPercent(outcome.value)}`}
                >
                  {outcome.key} {formatPercent(outcome.value)}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function outcomeRows(
  fixture: AdminFixtureRow,
  odds: { home: number; draw: number; away: number },
) {
  return [
    { key: 'H', label: fixture.home_team, value: odds.home },
    { key: 'D', label: 'Draw', value: odds.draw },
    { key: 'A', label: fixture.away_team, value: odds.away },
  ];
}

function averageOddsSources(
  sources: Array<{ home: number; draw: number; away: number }>,
): { home: number; draw: number; away: number } | null {
  if (sources.length === 0) return null;
  const totals = sources.reduce(
    (sum, source) => ({
      home: sum.home + source.home,
      draw: sum.draw + source.draw,
      away: sum.away + source.away,
    }),
    { home: 0, draw: 0, away: 0 },
  );
  return {
    home: totals.home / sources.length,
    draw: totals.draw / sources.length,
    away: totals.away / sources.length,
  };
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function FixtureDifficultyNote() {
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">FDR:</span> backend-owned enrichment from
      Fantasy Premier League's official fixture difficulty model. FPL says the rating is produced
      by its experts' algorithm using team performance statistics, home/away context, and recent
      home/away form over the past six fixtures. Values are 1 easiest to 5 hardest; unavailable
      means the backend could not match a fixture or fetch the source feed.
    </div>
  );
}

function FixtureDifficultyCell({ fixture }: { fixture: AdminFixtureRow }) {
  const home = fixture.home_difficulty;
  const away = fixture.away_difficulty;
  if (!isDifficulty(home) && !isDifficulty(away)) {
    return <span className="font-mono text-muted-foreground">{DASH}</span>;
  }

  const source =
    fixture.difficulty_source === 'fantasy_premier_league'
      ? 'Fantasy Premier League'
      : 'unknown source';

  return (
    <div
      className="flex flex-col gap-1 font-mono text-xs"
      title={`Backend FDR source: ${source}. Home and away values are team-specific.`}
    >
      <span>H {isDifficulty(home) ? home : DASH}</span>
      <span>A {isDifficulty(away) ? away : DASH}</span>
    </div>
  );
}

function isDifficulty(value: number | null | undefined): value is 1 | 2 | 3 | 4 | 5 {
  return value != null && Number.isInteger(value) && value >= 1 && value <= 5;
}

/**
 * Score column renderer. Shows the football-standard breakdown stacked
 * line-by-line so every stage of a knockout result is visible at once:
 *
 *   Group / regulation knockout : single row     "2 - 1"
 *   Knockout decided in ET      : two rows       "FT  1-1"
 *                                                "ET  2-1" (amber, decisive)
 *   Knockout decided on PSO     : three rows     "FT   1-1"
 *                                                "ET   2-2"
 *                                                "Pen  4-5" (violet, decisive)
 *
 * `home_score` / `away_score` are open-play totals (regulation + ET);
 * the PSO winner is conveyed by `won_via_penalty_shootout` + the
 * `penalty_shootout_*` columns rather than baked into the scoreline.
 */
function FixtureScore({ fixture }: { fixture: AdminFixtureRow }) {
  if (fixture.home_score == null || fixture.away_score == null) {
    return <span className="font-mono text-muted-foreground">{DASH}</span>;
  }

  const wentToET =
    fixture.extra_time_home_score != null && fixture.extra_time_away_score != null;
  const wentToPSO =
    fixture.won_via_penalty_shootout === true &&
    fixture.penalty_shootout_home != null &&
    fixture.penalty_shootout_away != null;

  // Single-stage match (groups + non-ET knockouts) — just the score.
  if (!wentToET && !wentToPSO) {
    return (
      <span className="font-mono font-semibold">
        {fixture.home_score} - {fixture.away_score}
      </span>
    );
  }

  // Multi-stage match — render every played stage on its own row, with
  // the decisive stage highlighted.
  const rows: Array<{ label: string; score: string; tone: 'muted' | 'amber' | 'violet' | 'plain' }> = [];

  if (fixture.regulation_home_score != null && fixture.regulation_away_score != null) {
    rows.push({
      label: 'FT',
      score: `${fixture.regulation_home_score}-${fixture.regulation_away_score}`,
      tone: wentToPSO || wentToET ? 'muted' : 'plain',
    });
  }
  if (wentToET) {
    rows.push({
      label: 'ET',
      score: `${fixture.extra_time_home_score}-${fixture.extra_time_away_score}`,
      tone: wentToPSO ? 'muted' : 'amber',
    });
  }
  if (wentToPSO) {
    rows.push({
      label: 'Pen',
      score: `${fixture.penalty_shootout_home}-${fixture.penalty_shootout_away}`,
      tone: 'violet',
    });
  }

  const toneClass = (tone: (typeof rows)[number]['tone']): string => {
    switch (tone) {
      case 'muted':
        return 'text-muted-foreground';
      case 'amber':
        return 'text-amber-700 dark:text-amber-300 font-semibold';
      case 'violet':
        return 'text-violet-700 dark:text-violet-300 font-semibold';
      default:
        return 'font-semibold';
    }
  };

  return (
    <div className="flex flex-col items-end gap-0 font-mono leading-tight">
      {rows.map((row) => (
        <div key={row.label} className={cn('flex items-baseline gap-2 text-xs', toneClass(row.tone))}>
          <span className="text-[10px] uppercase tracking-wide opacity-70">{row.label}</span>
          <span className="tabular-nums">{row.score}</span>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  if (s === 'live' || s === 'in_play') {
    return <Badge>Live</Badge>;
  }
  if (s === 'completed' || s === 'ft' || s === 'finished') {
    return <Badge variant="default">Completed</Badge>;
  }
  if (s === 'scheduled' || s === 'ns') {
    return <Badge variant="outline">Scheduled</Badge>;
  }
  if (s === 'postponed' || s === 'cancelled' || s === 'suspended' || s === 'abandoned') {
    return (
      <Badge variant="outline" className="text-yellow-600">
        {status}
      </Badge>
    );
  }
  return <Badge variant="outline">{status || DASH}</Badge>;
}

interface ReassignDialogProps {
  fixture: AdminFixtureRow | null;
  rounds: number[];
  onClose: () => void;
}

function ReassignDialog({ fixture, rounds, onClose }: ReassignDialogProps) {
  const readOnly = isReadOnlyAdminMode();
  const [target, setTarget] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const mutation = useReassignFixtureRound();

  // Reset form when dialog opens for a different fixture.
  const openId = fixture?.id ?? null;
  useResetOnChange(openId, () => {
    setTarget(fixture?.assigned_round != null ? String(fixture.assigned_round) : '');
    setReason('');
    mutation.reset();
  });

  const open = !!fixture;
  const currentEffective = fixture?.assigned_round ?? fixture?.round ?? null;
  const reassignRounds = useMemo(() => {
    const set = new Set(rounds);
    if (fixture?.round != null) set.add(fixture.round);
    if (fixture?.effective_round != null) set.add(fixture.effective_round);
    return Array.from(set).sort((a, b) => a - b);
  }, [fixture?.effective_round, fixture?.round, rounds]);

  const submit = () => {
    if (!fixture || readOnly) return;
    const newAssigned = target === '' ? null : Number(target);
    if (newAssigned !== null && Number.isNaN(newAssigned)) return;
    mutation.mutate(
      {
        fixtureId: fixture.id,
        assignedRound: newAssigned,
        reason: reason.trim() || undefined,
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reassign fixture to round</DialogTitle>
          <DialogDescription>
            Override which round this fixture's points score into. Leave blank to reset to
            the original round from SportsMonks.
          </DialogDescription>
        </DialogHeader>

        {fixture ? (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="font-medium">
                {fixture.home_team} vs {fixture.away_team}
              </div>
	              <div className="mt-1 text-xs text-muted-foreground">
	                Original: R{fixture.round} · Currently scoring in: R{currentEffective}
	                {fixture.scope_code ? ` · ${fixture.scope_code}` : ''}
	              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Reassign to round
              </label>
              <select
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">— Reset to default (R{fixture.round}) —</option>
                {reassignRounds.map((r) => (
                  <option key={r} value={r}>
                    R{r}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Reason (optional)
              </label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why this fixture moves rounds…"
                maxLength={512}
              />
            </div>

            {mutation.isError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {(mutation.error as Error)?.message ?? 'Reassign failed'}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={readOnly || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Re-run `cb` whenever `key` changes (used to reset dialog state when a
 * different fixture row opens the dialog).
 */
function useResetOnChange<K>(key: K, cb: () => void) {
  const last = useRef<K | undefined>(undefined);
  useEffect(() => {
    if (last.current !== key) {
      last.current = key;
      cb();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
